import type { GameV1 } from '@/api/games';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { Country } from '@/helpers/conf';
import { groupMember, USER_GROUP } from '@/helpers/user';
import type {
  ISearchResultAppearanceEventData,
  TSearchResultGameOpenType,
} from '@/modules/googleAnalytics/events/searchGamesEvents';
import { useCustomSearchScenario } from '@/modules/lobby/components/FilterGames/Search/hooks/useCustomSearchScenarios';
import { useGetCommonStore } from '@/store';
import type { ISearchGridOptions } from '../ResultWithSimilar';
import { CustomScenario } from './components/CustomScenario';
import { DefaultScenario } from './components/DefaultScenario';
import { JackpotScenario } from './components/JackpotScenario';
import { PennyScenario } from './components/PennyScenario';
import { PromocodeScenario } from './components/PromocodeScenario';
import { ProviderScenario } from './components/ProviderScenario';
import { ProviderScenarioCA } from './components/ProviderScenarioCA';
import { ThemeScenario } from './components/ThemeScenario';
import { WageringScenario } from './components/WageringScenario';
import { WithdrawScenario } from './components/WithdrawScenario';
import {
  checkIncludesKeywords,
  JACKPOT_SCENARIO_KEYWORD,
  PENNY_SCENARIO_KEYWORDS,
  PROMOCODES_SCENARIO_KEYWORDS,
  PROVIDERS_SCENARIO_AU_KEYWORD,
  PROVIDERS_SCENARIO_KEYWORDS,
  THEME_SCENARIO_KEYWORD,
  WAGERING_SCENARIO_KEYWORDS,
  WITHDRAWAL_SCENARIO_KEYWORDS,
} from './helpers';

export interface ISearchFallbackScenariosProps {
  searchPhrase: string;
  gridOptions: ISearchGridOptions;
  handleSearchGameClick: (
    game: GameV1,
    type: TSearchResultGameOpenType
  ) => void;
  sendSearchResultAppearance: (
    params: Partial<ISearchResultAppearanceEventData>
  ) => void;
  closeModal?: () => void;
}

export const SearchFallbackScenarios: React.FC<
  ISearchFallbackScenariosProps
> = (props) => {
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const countryCode = useCommonStore.use.countryCode() as Country;
  const searchPhrase = props.searchPhrase;
  const { scenario: customScenario, isLoading: isCustomScenarioLoading } =
    useCustomSearchScenario(searchPhrase);

  if (
    groupMember(USER_GROUP.PENNY)(user) &&
    checkIncludesKeywords(searchPhrase, PENNY_SCENARIO_KEYWORDS)
  ) {
    return <PennyScenario {...props} />;
  }

  const isCanada = countryCode
    .toLowerCase()
    .endsWith(Country.Canada.toLowerCase());
  const isProviderKeyword = checkIncludesKeywords(
    searchPhrase,
    PROVIDERS_SCENARIO_KEYWORDS
  );
  const isAUKeyword = checkIncludesKeywords(searchPhrase, [
    PROVIDERS_SCENARIO_AU_KEYWORD,
  ]);

  const isAustralia = countryCode
    .toLowerCase()
    .endsWith(Country.Australia.toLowerCase());

  if (isAUKeyword) {
    if (isCanada) return <ProviderScenarioCA {...props} />;
    if (user?.country === Country.Australia || !isAustralia) {
      return <ProviderScenario {...props} />;
    }
  }

  if (isProviderKeyword) {
    return <ProviderScenario {...props} />;
  }

  if (checkIncludesKeywords(searchPhrase, WAGERING_SCENARIO_KEYWORDS)) {
    return <WageringScenario {...props} />;
  }

  if (
    isAuthenticated &&
    checkIncludesKeywords(searchPhrase, WITHDRAWAL_SCENARIO_KEYWORDS)
  ) {
    return <WithdrawScenario {...props} />;
  }

  if (checkIncludesKeywords(searchPhrase, PROMOCODES_SCENARIO_KEYWORDS)) {
    return <PromocodeScenario {...props} />;
  }

  if (checkIncludesKeywords(searchPhrase, [THEME_SCENARIO_KEYWORD])) {
    return <ThemeScenario {...props} />;
  }

  if (checkIncludesKeywords(searchPhrase, [JACKPOT_SCENARIO_KEYWORD])) {
    return <JackpotScenario {...props} />;
  }

  if (isCustomScenarioLoading) return <GamesSkeleton isSearchModal />;

  if (customScenario) {
    return <CustomScenario scenario={customScenario} {...props} />;
  }

  return <DefaultScenario {...props} />;
};
